import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Search } from "lucide-react";
import { useState } from "react";
import type { UnmatchedLeadTerm } from "./useUnmatchedLeadTerms";
import { getLangfuseSpanUrl } from "@/ai-coding-outputs/langfuseUrl";

interface Props {
  term: UnmatchedLeadTerm;
  /** Called with synthetic code `unmatched::{leadTerm}` when user clicks highlight button */
  onToggleHighlight?: (syntheticCode: string) => void;
  /** Whether this term's quotes are currently highlighted (focused) */
  isFocused?: boolean;
}

export function UnmatchedLeadTermItem({ term, onToggleHighlight, isFocused }: Props) {
  const [expanded, setExpanded] = useState(false);
  const langfuseUrl =
    term.langfuseTraceId && term.langfuseSpanId ? getLangfuseSpanUrl(term.langfuseTraceId, term.langfuseSpanId) : null;

  return (
    <div className="border border-dashed border-muted-foreground/30 rounded-md bg-muted/30">
      <div className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left min-w-0"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          <span className="truncate font-medium">{term.leadTerm}</span>
        </button>
        {onToggleHighlight && term.evidenceQuotes.length > 0 && (
          <button
            type="button"
            className={`shrink-0 p-0.5 rounded ${isFocused ? "text-orange-500" : "text-gray-400 hover:text-gray-600"}`}
            onClick={() => onToggleHighlight(`unmatched::${term.leadTerm}`)}
            aria-label={`Toggle evidence highlight for ${term.leadTerm}`}
          >
            <Search className="h-3.5 w-3.5" />
          </button>
        )}
        <span className="shrink-0 text-xs text-muted-foreground/60">no code</span>
      </div>

      {expanded && (
        <div className="border-t border-dashed border-muted-foreground/20 px-3 py-2 text-xs text-muted-foreground space-y-2">
          {term.evidenceQuotes.length > 0 && (
            <div>
              <p className="font-medium mb-1">Evidence quotes:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {term.evidenceQuotes.map((q) => (
                  <li key={`${q.text}-${q.fileIdentifier}`}>
                    <span className="italic">&ldquo;{q.text}&rdquo;</span>
                    <span className="block text-muted-foreground/60 not-italic">— {q.fileIdentifier}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {term.termChain.length > 0 && (
            <div>
              <p className="font-medium mb-1">Term chain:</p>
              <ol className="list-decimal pl-4 space-y-0.5">
                {term.termChain.map((entry) => (
                  <li key={`${entry.level}-${entry.term}`}>
                    <span className="font-mono">{entry.term}</span>
                    {entry.evaluatedSubTerms.length > 0 && (
                      <span className="text-muted-foreground/60">
                        {" "}
                        (evaluated: {entry.evaluatedSubTerms.join(", ")})
                      </span>
                    )}
                    {entry.debugInfo && (
                      <div className="mt-1 mb-1 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1 text-[11px] text-slate-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed">
                        <span className="font-semibold text-slate-500 dark:text-slate-400">Reasoning: </span>
                        {entry.debugInfo}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {langfuseUrl && (
            <a
              href={langfuseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-500 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View in Langfuse
            </a>
          )}
        </div>
      )}
    </div>
  );
}
